// src/app/shared/services/query-view.service.ts
import { Injectable, computed, inject } from '@angular/core'
import { Observable } from 'rxjs'

import {
  GeneratedContent,
  Enums,
  PivotRecord,
  Environment,
  Tool,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools } from '@cyborg-monorepo/hunter/matchboi'

import { PivotAPIService } from '../api/pivot-api.service'
import { NavigationService } from '../navigation.service'
import { QueryIndexService } from '../query-index.service'
import { SessionService } from '../session.service'
import { SelectedQueryPath } from '../utils/navigation.utils'

import { HuntViewService } from './hunt-view.service'
@Injectable({ providedIn: 'root' })
export class QueryViewService {
  // ────────────────
  // Injected services
  // ────────────────
  private readonly huntSvc = inject(HuntViewService)
  private readonly nav = inject(NavigationService)
  private readonly pivotApi = inject(PivotAPIService)
  private readonly session = inject(SessionService)
  private readonly queryIdx = inject(QueryIndexService)

  // ────────────────
  // URL‐derived context signals
  // ────────────────
  readonly packageId = computed<string | null>(() => this.nav.packageId())
  readonly selectedQueryId = computed<string | null>(() =>
    this.nav.selectedQueryId(),
  )
  readonly toolId = computed<string | null>(() => this.nav.toolId())
  readonly pivotPath = computed<string[]>(() => this.nav.pivotPath())
  readonly environmentId = computed<string | null>(() =>
    this.nav.environmentId(),
  )

  readonly isPivotQuery = computed<boolean>(() => this.pivotPath().length > 0)

  readonly selectedQueryPath = computed<SelectedQueryPath | null>(() => {
    const huntQueryId = this.selectedQueryId()
    const toolId = this.toolId()
    return huntQueryId && toolId
      ? { toolId, huntQueryId, pivotIds: this.pivotPath() }
      : null
  })
  readonly selectedQuery = computed<GeneratedContent | null>(() => {
    const path = this.selectedQueryPath()
    const hunt = this.huntSvc.activeHunt()
    if (!path || !hunt) return null
    return this.queryIdx.select(hunt, path) ?? null
  })

  // ────────────────
  // Pivot API & helpers
  // ────────────────

  /** Cached list of all pivots (must call loadAllPivots()) */
  readonly allPivots = this.pivotApi.allPivots

  /** Kick off loading of the full pivot catalog */
  loadAllPivots(): void {
    this.pivotApi.loadAllPivots()
  }

  /** Fetch primary/secondary fields for a given hunt category */
  getPivotFields(category: Enums.HuntCategoryEnum): Observable<{
    primary_fields: string[]
    secondary_fields: string[]
  }> {
    return this.pivotApi.getFieldsForCategory(category)
  }

  /** Retrieve pivot‐match suggestions for a tool + chosen fields */
  getPivotMatches(
    tool: SupportedTools,
    fields: string[],
  ): Observable<PivotRecord[]> {
    return this.pivotApi.getPivotMatches(tool, fields)
  }

  // ────────────────
  // Context lookup (env/tool)
  // ────────────────

  readonly selectedEnvironment = computed<Environment | null>(() => {
    const pkg = this.huntSvc
      .huntPackages()
      .find((p) => p.UUID === this.packageId())
    return pkg?.analysis.find((e) => e.id === this.environmentId()) ?? null
  })

  readonly selectedToolId = computed<Tool | null>(() => {
    const env = this.selectedEnvironment()
    return env?.tools.find((t) => t.id === this.toolId()) ?? null
  })

  readonly selectedTool = computed<Tool | null>(() => {
    const pkg = this.huntSvc
      .huntPackages()
      .find((p) => p.UUID === this.packageId())
    const tools = pkg?.analysis.flatMap((e) => e.tools ?? []) ?? []
    return tools.find((t) => t.id === this.toolId()) ?? null
  })

  readonly selectedToolType = computed<SupportedTools | null>(() => {
    return (this.selectedTool()?.product as SupportedTools) ?? null
  })

  /** the original query/pivot before any edits */
  readonly originalContent = computed<GeneratedContent | null>(() => {
    const path = this.selectedQueryPath()
    const orig = this.session.originalHunt()
    if (!path || !orig) return null
    return this.queryIdx.select(orig, path) ?? null
  })

  /**
   * Retrieve the raw query string from the originally loaded hunt.
   * Follows the current package/tool/query selection and optional pivot path.
   */
  originalQuery(): string | null {
    const hunt = this.session.originalHunt()
    const pkgId = this.packageId()
    const toolId = this.toolId()
    const queryId = this.selectedQueryId()
    if (!hunt || !pkgId || !toolId || !queryId) return null

    const pkg = hunt.huntPackages.find((p) => p.UUID === pkgId)
    if (!pkg) return null

    const tools = pkg.originalAnalysis?.flatMap((env) => env.tools ?? []) ?? []
    const tool = tools.find((t) => t.id === toolId)
    if (!tool) return null

    let node = tool.generatedContent?.Hunt?.find((q) => q.id === queryId)
    if (!node) return null

    for (const pivotId of this.pivotPath()) {
      node = node.pivots?.find((p) => p.id === pivotId)
      if (!node) return null
    }

    return node.query ?? null
  }
}
